export interface LatLng {
  lat: number;
  lng: number;
}

export interface GeoJsonPoint {
  type: 'Point';
  coordinates: [number, number];
}

export interface GeoJsonPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

export function latLngToGeoJsonPoint(lat: number, lng: number): GeoJsonPoint {
  return {
    type: 'Point',
    coordinates: [lng, lat],
  };
}

export function geoJsonPointToLatLng(point: GeoJsonPoint): LatLng {
  return {
    lat: point.coordinates[1],
    lng: point.coordinates[0],
  };
}

export function geoJsonPolygonToCoordinates(polygon: GeoJsonPolygon): number[][] {
  return polygon.coordinates[0] || [];
}

export function coordinatesToGeoJsonPolygon(coordinates: number[][]): GeoJsonPolygon {
  if (coordinates.length < 3) {
    throw new Error('Polygon must have at least 3 points');
  }

  const firstPoint = coordinates[0];
  const lastPoint = coordinates[coordinates.length - 1];

  if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
    coordinates = [...coordinates, [firstPoint[0], firstPoint[1]]];
  }

  return {
    type: 'Polygon',
    coordinates: [coordinates],
  };
}

export function calculatePolygonArea(coordinates: number[][]): number {
  if (coordinates.length < 3) {
    return 0;
  }

  const R = 6371;
  let area = 0;

  for (let i = 0; i < coordinates.length - 1; i++) {
    const p1 = coordinates[i];
    const p2 = coordinates[i + 1];
    
    const lat1 = (p1[1] * Math.PI) / 180;
    const lat2 = (p2[1] * Math.PI) / 180;
    const dLon = ((p2[0] - p1[0]) * Math.PI) / 180;

    area +=
      (dLon / 2) *
      (Math.sin(2 * lat1) + Math.sin(2 * lat2)) *
      R * R;
  }

  return Math.abs(area);
}

export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

export function isPointInPolygon(lng: number, lat: number, polygon: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];

    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
